import React, {
	CSSProperties,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";

import {
	getReadMoreLabel,
	READ_MORE_LINE_HEIGHT,
	RICH_POST_COLLAPSED_LINES,
} from "components/ReadMore/readMorePresentation";
import styles from "./styles.module.scss";

interface IRichPostBody {
	html: string;
	// #1428 When true, clamp a long body and show a "See more / See less"
	// toggle (mirrors the plain-text path). The caller decides based on the
	// text length so image-only / short posts are never clamped.
	collapsible?: boolean;
}

/**
 * Renders a rich-text (HTML) feed post body (#1269). Uses the browser's own
 * parser via dangerouslySetInnerHTML — the content is already sanitized
 * upstream (server on store + convertMentionIntoHref on render). This avoids
 * the react-html-replace parser, which chokes on real formatting markup
 * (<p>/<ul>/...). Mentions stay clickable via event delegation.
 */
const RichPostBody: React.FunctionComponent<IRichPostBody> = ({
	html,
	collapsible = false,
}) => {
	const navigate = useNavigate();
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [expanded, setExpanded] = useState(false);
	const [overflowing, setOverflowing] = useState(false);

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const mention = (e.target as HTMLElement)?.closest?.(
				"[membermentionid]",
			);
			if (mention) {
				e.preventDefault();
				e.stopPropagation();
				const id = mention.getAttribute("membermentionid");
				if (id) navigate(`/members/${id}`);
			}
		},
		[navigate],
	);

	// Decide whether the clamped body actually overflows (only then do we need
	// the toggle). Measured after layout settles; re-checked when the html
	// changes (e.g. after edit).
	useEffect(() => {
		setExpanded(false);
		if (!collapsible || !contentRef.current) {
			setOverflowing(false);
			return;
		}
		const rafId = requestAnimationFrame(() => {
			if (contentRef.current) {
				setOverflowing(
					contentRef.current.scrollHeight >
						RICH_POST_COLLAPSED_LINES * READ_MORE_LINE_HEIGHT + 1,
				);
			}
		});
		return () => cancelAnimationFrame(rafId);
	}, [collapsible, html]);

	const isClamped = collapsible && overflowing && !expanded;

	return (
		<>
			<div
				ref={contentRef}
				className={`${styles.richBody} ${
					isClamped ? styles.clamped : ""
				}`}
				onClick={onClick}
				style={
					isClamped
						? ({
								"--rich-post-collapsed-lines":
									RICH_POST_COLLAPSED_LINES,
						  } as CSSProperties)
						: undefined
				}
				dangerouslySetInnerHTML={{ __html: html }}
			/>
			{collapsible && overflowing && (
				<button
					type="button"
					className={styles.seeMore}
					onClick={() => setExpanded((v) => !v)}
					aria-expanded={expanded}
				>
					{getReadMoreLabel(expanded)}
				</button>
			)}
		</>
	);
};

export default memo(RichPostBody);
